"use client";

import { useNavigateAndHideMenu } from "@faire/deprecated-retailer-visitor-shared/components/Header/mobile/Navigation/useNavigateAndHideMenu";
import { SignUpType } from "@faire/deprecated-retailer-visitor-shared/lib/popSignUp";
import {
  useAssignSettingEnableDiscoverCategoryCTABanners,
  useSettingEnableDiscoverCategoryCTABanners,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableDiscoverCategoryCTABanners";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { BottomDrawer } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/BottomDrawer";
import { IThreeVariantXP } from "@faire/web-api--source/indigofair/settings/IThreeVariantXP";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { throttleIfPossible } from "@faire/web/common/globals/throttleIfPossible";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Input } from "@faire/web/slate-deprecated/Input";
import { Row } from "@faire/web/slate/Layout";
import { Flex } from "@faire/web/slate/Layout/Flex";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

const TIMEOUT_THRESHOLD = 2000;
export const LoggedOutSignUpMiniDrawer: React.FC = () => {
  const { navigateToSignUp } = useNavigateAndHideMenu();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const hasStartedTimerRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [emailAddress, setEmailAddress] = useState("");
  const discoverCategoryCTABannersVariant =
    useSettingEnableDiscoverCategoryCTABanners();
  const assignSettingEnableDiscoverCategoryCTABanners =
    useAssignSettingEnableDiscoverCategoryCTABanners();
  const { strictLocalize } = useStrictLocalization();

  const throttledHandleScroll = useMemo(
    () =>
      throttleIfPossible(() => {
        const shouldShowDrawer = getShouldShowDrawer();

        if (shouldShowDrawer) {
          assignSettingEnableDiscoverCategoryCTABanners();
        }
        if (shouldShowDrawer && !hasStartedTimerRef.current && !isDrawerOpen) {
          // Only start timer once when user first scrolls
          hasStartedTimerRef.current = true;

          timeoutRef.current = setTimeout(() => {
            setIsDrawerOpen(true);
          }, TIMEOUT_THRESHOLD);
        }
      }, 300),
    [isDrawerOpen, assignSettingEnableDiscoverCategoryCTABanners]
  );

  useEffect(() => {
    const handleScroll = throttledHandleScroll;
    getWindow()?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      getWindow()?.removeEventListener("scroll", handleScroll);
      // Clean up timeout on unmount
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [throttledHandleScroll]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  };

  if (discoverCategoryCTABannersVariant !== IThreeVariantXP.Bucket.VARIANT_2) {
    return null;
  }

  return (
    <BottomDrawer isOpen={isDrawerOpen}>
      <Flex
        align="center"
        justify="space-between"
        className="gap-y-4 px-5 py-5 md:gap-x-[100px] md:gap-y-0"
        direction={{
          mobileAndAbove: "column",
          tabletAndAbove: "row",
        }}
      >
        <Typography>
          <StrictLocalMsg
            id="epZ9PM"
            defaultMessage="Sign up to unlock customized recommendations at wholesale prices"
            description={{
              text: "Prompt for sign up benefits",
              img: "https://cdn.faire.com/fastly/651ba5b88b83421c2c2abbfa0a6a05916fa771388b3c60daa0be724389eb882d.png",
            }}
          />
        </Typography>
        <Row align="center" justify="center" className="w-full md:w-auto">
          <Input
            type="email"
            height="52px"
            placeholder={strictLocalize({
              id: "w25cfD",
              defaultMessage: "Business email address",
              description: {
                text: "placeholder for Email address input field",
              },
            })}
            autoComplete="email"
            value={emailAddress}
            onInput={handleEmailChange}
            data-test-id="mini-drawer-email-input"
            className="h-[48px]! min-w-[200px] lg:min-w-[300px]"
          />
          <Button
            width="fit-content"
            className="whitespace-nowrap"
            onClick={() =>
              navigateToSignUp(
                undefined,
                {
                  sourceId: "mini_drawer_signup_cta",
                },
                SignUpType.GENERIC
              )
            }
            data-test-id="miniDrawerSignUpButton"
          >
            <StrictLocalMsg
              id="7m/igv"
              defaultMessage="Sign up"
              description={{
                text: "Sign up button",
              }}
            />
          </Button>
        </Row>
      </Flex>
    </BottomDrawer>
  );
};

const getShouldShowDrawer = () => {
  const window = getWindow();
  const scrollPosition = window?.scrollY ?? 0;
  return scrollPosition > 0;
};
